import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Container, Row, Col, Card, Button, Image } from "react-bootstrap";
import Menu from "../sections/Menu";
import Footer from "../sections/Footer";
import Partners from "../sections/Partners";
import FAQ from "../sections/FAQ";
import AnimatedCounter from "../components/AnimatedCounter";
import { Settings, Calculator, Clock, MapPin, Calendar, FileText, BarChart3, FileDown, Smartphone, Headphones, Palmtree, Camera } from "lucide-react";
import { AnalyticsHandle } from "../components/AnalyticsHandle";
import PageMeta from "../seo/PageMeta";
import { buildFaqStructuredData, buildServiceStructuredData } from "../seo/structuredData";

function Pricing2() {
  const location = useLocation();
  const [selectedSegment, setSelectedSegment] = useState("msp");
  const [billingCycle, setBillingCycle] = useState("yearly");
  const [gpsEnabled, setGpsEnabled] = useState(false);
  const [leaveEnabled, setLeaveEnabled] = useState(false);
  const [employeeCount, setEmployeeCount] = useState(10);
  const [highlightedRange, setHighlightedRange] = useState(null);
  const [content, setContent] = useState({});
  const { logRegisterClick } = AnalyticsHandle();

  const handleRegisterClick = (source = "pricing_primary") => {
    logRegisterClick(source);
    window.open("/register", "_self");
  };

  useEffect(() => {
    // Load main content
    fetch("/data/content.json")
      .then((res) => res.json())
      .then((data) => {
        // Load pricing-specific FAQ
        fetch("/data/pricing-content.json")
          .then((res) => res.json())
          .then((pricingData) => {
            setContent({
              ...data,
              faq: pricingData.faq,
            });
          })
          .catch((error) => {
            console.error("Error loading pricing FAQ:", error);
            setContent(data);
          });
      })
      .catch((error) => console.error("Error loading JSON:", error));
  }, []);

  const pageStructuredData = useMemo(
    () =>
      [
        buildFaqStructuredData(content, location.pathname),
        buildServiceStructuredData({
          path: location.pathname,
          name: "Cennik eGodziny",
          description:
            "Plany i pakiety systemu eGodziny dla firm różnej wielkości.",
          serviceType: "Subskrypcja systemu ewidencji czasu pracy",
        }),
      ].filter(Boolean),
    [content, location.pathname]
  );

  const mspPricing = {
    monthly: {
      base: 199,
      perUser: 19,
      gps: 5,
      leave: 6
    },
    yearly: {
      base: 159,
      perUser: 15,
      gps: 4,
      leave: 5
    }
  };

  const enterprisePricing = [
    { range: "100–200", price: 1490 },
    { range: "200–300", price: 1990 },
    { range: "300–500", price: 2990 },
    { range: "500+", price: "Wycena indywidualna" }
  ];

  const currentMspPricing = mspPricing[billingCycle];

  // Obliczanie ceny per user
  const calculatePerUserPrice = () => {
    let price = currentMspPricing.perUser;
    if (gpsEnabled) price += currentMspPricing.gps;
    if (leaveEnabled) price += currentMspPricing.leave;
    return price.toFixed(2);
  };

  // Obliczanie całkowitej ceny dla MŚP
  const calculateTotalPrice = () => {
    const perUserPrice = parseFloat(calculatePerUserPrice());
    return (currentMspPricing.base + (perUserPrice * employeeCount)).toFixed(2);
  };

  // Obsługa zmiany liczby pracowników
  const handleEmployeeCountChange = (value) => {
    const count = parseInt(value) || 0;
    setEmployeeCount(count);

    if (count > 100) {
      setSelectedSegment("enterprise");
      // Określ zakres dla Enterprise
      if (count >= 100 && count <= 200) {
        setHighlightedRange("100–200");
      } else if (count > 200 && count <= 300) {
        setHighlightedRange("200–300");
      } else if (count > 300 && count <= 500) {
        setHighlightedRange("300–500");
      } else if (count > 500) {
        setHighlightedRange("500+");
      }
    } else {
      setHighlightedRange(null);
    }
  };

  return (
    <>
      <PageMeta path={location.pathname} structuredData={pageStructuredData} />
      <Menu />
      <Container className="py-5" style={{ paddingTop: "120px", minHeight: "70vh", marginBottom:"60px" }}>
        <div className="text-center">
          <h1 style={{ fontSize: "2.5rem", fontWeight: "700", color: "#1f2937", marginBottom: "4px" }}>
            Cennik e<span style={{ color: "#407EF9" }}>G</span>odziny
          </h1>
          <p style={{ fontSize: "1.1rem", color: "#6b7280", maxWidth: "700px", margin: "0 auto 16px", paddingBottom: "20px" }}>
            Dopasowany do wielkości Twojej firmy
          </p>
        </div>

        {/* Segmented Control */}
        <div className="d-flex justify-content-center" style={{ marginBottom: "6px" }}>
          <div style={{
            display: "inline-flex",
            backgroundColor: "#f3f4f6",
            borderRadius: "12px",
            padding: "4px",
            gap: "4px"
          }}>
            <button
              onClick={() => setSelectedSegment("msp")}
              style={{
                padding: "12px 40px",
                border: "none",
                borderRadius: "8px",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease",
                backgroundColor: selectedSegment === "msp" ? "#407EF9" : "transparent",
                color: selectedSegment === "msp" ? "white" : "#6b7280"
              }}
            >
              Mniejsze firmy (do 100 pracowników)
            </button>
            <button
              onClick={() => setSelectedSegment("enterprise")}
              style={{
                padding: "12px 40px",
                border: "none",
                borderRadius: "8px",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease",
                backgroundColor: selectedSegment === "enterprise" ? "#407EF9" : "transparent",
                color: selectedSegment === "enterprise" ? "white" : "#6b7280"
              }}
            >
              Duże organizacje (100+)
            </button>
          </div>
        </div>

        {/* Karty cennikowe */}
        {selectedSegment === "msp" ? (
          <>
            {/* Copy - Elastyczny model pod Segmented Control */}
            <div className="text-center" style={{ marginTop: "6px", paddingBottom: "60px" }}>
              <p style={{
                fontSize: "0.95rem",
                color: "#407EF9",
                fontWeight: "550",
                margin: "0 auto"
              }}>
                Płacisz tylko za to, czego używasz.
              </p>
            </div>

            <Row className="justify-content-center">
            {/* LEWA KOLUMNA - Konfiguracja */}
            <Col lg={6} className="mb-4">
              <div style={{
                backgroundColor: "#fff",
                border: "1.71px solid #27272B",
                borderRadius: "10.26px",
                padding: "29.925px",
                height: "100%",
                marginBottom: "48px"
              }}>
                <h3 style={{
                  fontSize: "1.197rem",
                  fontWeight: "700",
                  color: "#1f2937",
                  marginBottom: "21.375px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10.26px"
                }}>
                  <Settings size={23.94} color="#407EF9" />
                  Konfiguracja
                </h3>

                {/* Sposób rejestracji - RADIO */}
                <div style={{ marginBottom: "25.65px" }}>
                  <label style={{
                    display: "block",
                    fontSize: "0.855rem",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "10.26px"
                  }}>
                    Sposób rejestracji czasu:
                  </label>

                  <div style={{ marginBottom: "10.26px" }}>
                    <label style={{
                      display: "block",
                      cursor: "pointer",
                      padding: "10.26px",
                      border: !gpsEnabled ? "1.71px solid #407EF9" : "0.855px solid #d1d5db",
                      borderRadius: "6.84px",
                      backgroundColor: !gpsEnabled ? "#f0f7ff" : "transparent",
                      transition: "all 0.2s ease"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", marginBottom: "6.84px" }}>
                        <input
                          type="radio"
                          name="registrationType"
                          checked={!gpsEnabled}
                          onChange={() => setGpsEnabled(false)}
                          style={{
                            marginRight: "8.55px",
                            width: "15.39px",
                            height: "15.39px",
                            cursor: "pointer"
                          }}
                        />
                        <Clock size={17.1} color={!gpsEnabled ? "#407EF9" : "#6b7280"} strokeWidth={2.5} style={{ marginRight: "6.84px" }} />
                        <span style={{ fontSize: "0.81225rem", color: "#374151", fontWeight: "600" }}>
                          Deklarowanie
                        </span>
                        <span style={{
                          fontSize: "0.72675rem",
                          color: "#407EF9",
                          marginLeft: "6.84px",
                          fontWeight: "500"
                        }}>
                          ({currentMspPricing.perUser} zł / pracownik)
                        </span>
                      </div>
                      <p style={{
                        fontSize: "0.72675rem",
                        color: "#6b7280",
                        margin: "0 0 0 23.94px",
                        lineHeight: "1.5"
                      }}>
                        Pracownicy samodzielnie wpisują liczbę przepracowanych godzin i projekt. Idealne w małych firmach z zaufanym zespołem.
                      </p>
                    </label>
                  </div>

                  <div>
                    <label style={{
                      display: "block",
                      cursor: "pointer",
                      padding: "10.26px",
                      border: gpsEnabled ? "1.71px solid #407EF9" : "0.855px solid #d1d5db",
                      borderRadius: "6.84px",
                      backgroundColor: gpsEnabled ? "#f0f7ff" : "transparent",
                      transition: "all 0.2s ease"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", marginBottom: "6.84px" }}>
                        <input
                          type="radio"
                          name="registrationType"
                          checked={gpsEnabled}
                          onChange={() => setGpsEnabled(true)}
                          style={{
                            marginRight: "8.55px",
                            width: "15.39px",
                            height: "15.39px",
                            cursor: "pointer"
                          }}
                        />
                        <MapPin size={17.1} color={gpsEnabled ? "#407EF9" : "#6b7280"} strokeWidth={2.5} style={{ marginRight: "6.84px" }} />
                        <span style={{ fontSize: "0.81225rem", color: "#374151", fontWeight: "600" }}>
                          Start-Stop + GPS + Grafik
                        </span>
                        <span style={{
                          fontSize: "0.72675rem",
                          color: "#407EF9",
                          marginLeft: "6.84px",
                          fontWeight: "500"
                        }}>
                          (+{currentMspPricing.gps} zł / pracownik)
                        </span>
                      </div>
                      <p style={{
                        fontSize: "0.72675rem",
                        color: "#6b7280",
                        margin: "0 0 0 23.94px",
                        lineHeight: "1.5"
                      }}>
                        Rejestracja czasu po kliknięciu przycisku start/stop, z weryfikacją lokalizacji GPS. Daje pełną kontrolę nad obecnością w miejscu pracy.
                      </p>
                    </label>
                  </div>
                </div>

                {/* Nagłówek - Dodatkowe moduły */}
                <div style={{ marginBottom: "25.65px" }}>
                  <label style={{
                    display: "block",
                    fontSize: "0.855rem",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "10.26px"
                  }}>
                    Dodatkowe moduły:
                  </label>
                </div>

                {/* Wnioski urlopowe - CHECKBOX */}
                <div style={{ marginBottom: "25.65px" }}>
                  <label style={{
                    display: "block",
                    cursor: "pointer",
                    padding: "10.26px",
                    border: leaveEnabled ? "1.71px solid #407EF9" : "0.855px solid #d1d5db",
                    borderRadius: "6.84px",
                    backgroundColor: leaveEnabled ? "#f0f7ff" : "transparent",
                    transition: "all 0.2s ease"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "6.84px" }}>
                      <input
                        type="checkbox"
                        checked={leaveEnabled}
                        onChange={() => setLeaveEnabled(!leaveEnabled)}
                        style={{
                          marginRight: "8.55px",
                          width: "15.39px",
                          height: "15.39px",
                          cursor: "pointer"
                        }}
                      />
                      <Palmtree size={17.1} color={leaveEnabled ? "#407EF9" : "#6b7280"} strokeWidth={2.5} style={{ marginRight: "6.84px" }} />
                      <span style={{ fontSize: "0.81225rem", color: "#374151", fontWeight: "600" }}>
                        Wnioski urlopowe
                      </span>
                      <span style={{
                        fontSize: "0.72675rem",
                        color: "#407EF9",
                        marginLeft: "6.84px",
                        fontWeight: "500"
                      }}>
                        (+{currentMspPricing.leave} zł / pracownik)
                      </span>
                    </div>
                    <p style={{
                      fontSize: "0.72675rem",
                      color: "#6b7280",
                      margin: "0 0 0 23.94px",
                      lineHeight: "1.5"
                    }}>
                      Składanie i zatwierdzanie wniosków urlopowych online. Automatyczne naliczanie dni wolnych i powiadomienia dla przełożonych.
                    </p>
                  </label>
                </div>

                {/* Co zawiera system */}
                <div style={{
                  borderTop: "0.855px solid #e5e7eb",
                  paddingTop: "21.375px"
                }}>
                  <h4 style={{
                    fontSize: "0.9405rem",
                    fontWeight: "700",
                    color: "#1f2937",
                    marginBottom: "17.1px",
                    textAlign: "center"
                  }}>
                    W cenie otrzymujesz:
                  </h4>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10.26px"
                  }}>
                    {[
                      { name: "Ewidencja czasu pracy", icon: Clock, enabled: true },
                      { name: "GPS", icon: MapPin, enabled: gpsEnabled },
                      { name: "Grafik", icon: Calendar, enabled: gpsEnabled },
                      { name: "Wnioski urlopowe", icon: Palmtree, enabled: leaveEnabled },
                      { name: "Podsumowanie dnia", icon: FileText, enabled: true },
                      { name: "Raporty i analityka", icon: BarChart3, enabled: true },
                      { name: "Eksport do PDF i Excel", icon: FileDown, enabled: true },
                      { name: "Notatka ze zdjęciem", icon: Camera, enabled: true },
                      { name: "Aplikacja mobilna", icon: Smartphone, enabled: true },
                      { name: "Wsparcie techniczne", icon: Headphones, enabled: true }
                    ].map((feature, idx) => {
                      const IconComponent = feature.icon;
                      const isEnabled = feature.enabled;

                      return (
                        <div key={idx} style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8.55px",
                          backgroundColor: isEnabled ? "#f8f9fa" : "#f3f4f6",
                          padding: "10.26px",
                          borderRadius: "6.84px",
                          border: `0.855px solid ${isEnabled ? "#e5e7eb" : "#d1d5db"}`,
                          transition: "all 0.2s ease",
                          opacity: isEnabled ? 1 : 0.5
                        }}
                        onMouseEnter={(e) => {
                          if (isEnabled) {
                            e.currentTarget.style.backgroundColor = "#f0f7ff";
                            e.currentTarget.style.borderColor = "#407EF9";
                            e.currentTarget.style.transform = "translateY(-1.71px)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (isEnabled) {
                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                            e.currentTarget.style.borderColor = "#e5e7eb";
                            e.currentTarget.style.transform = "translateY(0)";
                          }
                        }}>
                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0
                          }}>
                            <IconComponent
                              size={17.1}
                              color={isEnabled ? "#407EF9" : "#9ca3af"}
                              strokeWidth={2.5}
                            />
                          </div>
                          <span style={{
                            fontSize: "0.7695rem",
                            color: isEnabled ? "#374151" : "#6b7280",
                            fontWeight: "500"
                          }}>{feature.name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Copy pod "W cenie otrzymujesz" */}
                <div style={{
                  borderTop: "0.855px solid #e5e7eb",
                  paddingTop: "21.375px",
                  marginTop: "21.375px",
                  textAlign: "center"
                }}>
                  <p style={{
                    fontSize: "0.9rem",
                    color: "#6b7280",
                    fontWeight: "500",
                    margin: "0"
                  }}>
                    Wspólnie ustalimy, czego dokładnie potrzebuje Twoja firma
                  </p>
                </div>
              </div>
            </Col>

            {/* PRAWA KOLUMNA - Cena */}
            <Col lg={6} className="mb-4">
              <div style={{
                background: "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
                border: "1.71px solid #e5e7eb",
                borderRadius: "10.26px",
                padding: "29.925px",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                marginBottom: "48px"
              }}>
                <h3 style={{
                  fontSize: "1.197rem",
                  fontWeight: "700",
                  color: "#1f2937",
                  marginBottom: "21.375px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10.26px"
                }}>
                  <Calculator size={23.94} color="#407EF9" />
                  Cena
                </h3>

                {/* Liczba pracowników - SLIDER */}
                <div style={{ marginBottom: "25.65px" }}>
                  <label style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: "0.7695rem",
                    fontWeight: "600",
                    color: "#6b7280",
                    marginBottom: "12.825px"
                  }}>
                    <span>Liczba pracowników:</span>
                    <span style={{
                      fontSize: "1.1115rem",
                      color: "#407EF9",
                      fontWeight: "700"
                    }}>
                      {employeeCount}
                    </span>
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={employeeCount}
                    onChange={(e) => handleEmployeeCountChange(e.target.value)}
                    style={{
                      width: "100%",
                      height: "6.84px",
                      borderRadius: "4.275px",
                      outline: "none",
                      background: `linear-gradient(to right, #407EF9 0%, #407EF9 ${(employeeCount - 1) / 99 * 100}%, #e5e7eb ${(employeeCount - 1) / 99 * 100}%, #e5e7eb 100%)`,
                      WebkitAppearance: "none",
                      appearance: "none",
                      cursor: "pointer"
                    }}
                    onMouseDown={(e) => {
                      const style = document.createElement('style');
                      style.innerHTML = `
                        input[type="range"]::-webkit-slider-thumb {
                          -webkit-appearance: none;
                          appearance: none;
                          width: 17.1px;
                          height: 17.1px;
                          border-radius: 50%;
                          background: #407EF9;
                          cursor: pointer;
                          border: 2.565px solid white;
                          box-shadow: 0 1.71px 5.13px rgba(0,0,0,0.2);
                        }
                        input[type="range"]::-moz-range-thumb {
                          width: 17.1px;
                          height: 17.1px;
                          border-radius: 50%;
                          background: #407EF9;
                          cursor: pointer;
                          border: 2.565px solid white;
                          box-shadow: 0 1.71px 5.13px rgba(0,0,0,0.2);
                        }
                      `;
                      if (!document.getElementById('range-slider-style')) {
                        style.id = 'range-slider-style';
                        document.head.appendChild(style);
                      }
                    }}
                  />
                  {employeeCount > 100 && (
                    <div style={{
                      marginTop: "8.55px",
                      padding: "8.55px",
                      backgroundColor: "#fef3c7",
                      borderRadius: "5.13px",
                      fontSize: "0.72675rem",
                      color: "#92400e"
                    }}>
                      💡 Dla firm powyżej 100 pracowników polecamy pakiety Enterprise
                    </div>
                  )}
                </div>

                {/* Separator górny */}
                <div style={{
                  borderTop: "0.855px solid #e5e7eb",
                  marginBottom: "21.375px"
                }}></div>

                {/* Twoja konfiguracja */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8.55px",
                  marginBottom: "10.26px"
                }}>
                  <Settings size={20.52} color="#407EF9" />
                  <div style={{
                    fontSize: "0.855rem",
                    color: "#374151",
                    fontWeight: "600"
                  }}>
                    Twoja konfiguracja:
                  </div>
                </div>

                <div style={{
                  fontSize: "1.197rem",
                  fontWeight: "700",
                  color: "#407EF9",
                  textAlign: "center",
                  marginBottom: "22.5px"
                }}>
                  {calculatePerUserPrice()} zł <span style={{ color: "#000000" }}>/</span> pracownik <span style={{ color: "#000000" }}>/</span> miesiąc
                </div>

                {/* Separator dolny */}
                <div style={{
                  borderTop: "0.855px solid #e5e7eb",
                  marginBottom: "21.375px"
                }}></div>

                {/* Przełącznik Miesięcznie / Rocznie */}
                <div style={{
                  marginBottom: "21.375px",
                  marginTop: "19px",
                  display: "flex",
                  justifyContent: "center"
                }}>
                  <div style={{
                    display: "inline-flex",
                    backgroundColor: "#f3f4f6",
                    borderRadius: "50px",
                    padding: "3.8px",
                    gap: "3.8px"
                  }}>
                    <button
                      onClick={() => setBillingCycle("monthly")}
                      style={{
                        padding: "9.5px 22.8px",
                        border: "none",
                        borderRadius: "50px",
                        fontSize: "0.855rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        backgroundColor: billingCycle === "monthly" ? "#ffffff" : "transparent",
                        color: billingCycle === "monthly" ? "#1f2937" : "#6b7280",
                        boxShadow: billingCycle === "monthly" ? "0 1.9px 3.8px rgba(0,0,0,0.1)" : "none"
                      }}
                    >
                      Plan miesięczny
                    </button>
                    <button
                      onClick={() => setBillingCycle("yearly")}
                      style={{
                        padding: "9.5px 22.8px",
                        border: "none",
                        borderRadius: "50px",
                        fontSize: "0.855rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        backgroundColor: billingCycle === "yearly" ? "#ffffff" : "transparent",
                        color: billingCycle === "yearly" ? "#1f2937" : "#6b7280",
                        boxShadow: billingCycle === "yearly" ? "0 1.9px 3.8px rgba(0,0,0,0.1)" : "none",
                        display: "flex",
                        alignItems: "center",
                        gap: "7.6px"
                      }}
                    >
                      Plan roczny
                      <span style={{
                        fontSize: "0.7125rem",
                        color: "#10b981",
                        fontWeight: "700",
                        backgroundColor: "#dcfce7",
                        padding: "1.9px 7.6px",
                        borderRadius: "11.4px"
                      }}>
                        -20%
                      </span>
                    </button>
                  </div>
                </div>

                {/* RAMKA - Razem miesięcznie */}
                <div style={{
                  backgroundColor: "#ffffff",
                  border: "2.565px solid #e5e7eb",
                  borderRadius: "10.26px",
                  padding: "21.375px",
                  marginBottom: "21.375px",
                  textAlign: "center",
                  boxShadow: "0 6.84px 20.52px rgba(229, 231, 235, 0.25)"
                }}>
                  <div style={{
                    fontSize: "0.7695rem",
                    color: "#6b7280",
                    marginBottom: "8.55px",
                    textTransform: "uppercase",
                    letterSpacing: "0.855px",
                    fontWeight: "600"
                  }}>
                    RAZEM MIESIĘCZNIE
                  </div>
                  <div style={{
                    fontSize: "3.42rem",
                    fontWeight: "800",
                    color: "#000000",
                    lineHeight: "1.1"
                  }}>
                    {calculateTotalPrice()}
                    <span style={{ fontSize: "1.71rem" }}> zł</span>
                  </div>
                </div>

                {/* W tym - rozbicie */}
                <div style={{
                  fontSize: "0.7695rem",
                  color: "#6b7280",
                  marginBottom: "21.375px",
                  textAlign: "left",
                  paddingLeft: "17.1px"
                }}>
                  <div style={{ marginBottom: "4.275px", fontWeight: "600" }}>w tym:</div>
                  <div style={{ marginBottom: "6.84px" }}>
                    • {currentMspPricing.base} zł opłata bazowa
                  </div>
                  <div>
                    • {employeeCount} × {calculatePerUserPrice()} zł za pracowników
                  </div>
                </div>

                {/* Separator nad CTA */}
                <div style={{
                  borderTop: "0.855px solid #e5e7eb",
                  marginBottom: "21.375px"
                }}></div>

                {/* CTA */}
                <Button
                  className="button-default w-100"
                  style={{
                    fontSize: "1.1rem",
                    padding: "16px 24px",
                    marginTop: "24px",
                    fontWeight: "600",
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(64, 126, 249, 0.2)",
                    transition: "all 0.3s ease"
                  }}
                  onClick={() => handleRegisterClick("pricing_calculator_primary")}
                  onMouseEnter={(e) => e.target.style.transform = "translateY(-2px)"}
                  onMouseLeave={(e) => e.target.style.transform = "translateY(0)"}
                >
                  Wypróbuj za darmo przez 14 dni
                </Button>

                {/* Informacja o okresie próbnym */}
                <div className="text-center" style={{ 
                  marginTop: "20px",
                  padding: "12px",
                  background: "#f9fafb",
                  borderRadius: "8px"
                }}>
                  <p style={{ 
                    fontSize: "0.875rem", 
                    color: "#374151",
                    margin: 0,
                    fontWeight: "500"
                  }}>
                    ✓ <strong>14-dniowy okres próbny</strong> bez podawania karty płatniczej
                  </p>
                </div>
              </div>
            </Col>
          </Row>
          </>
        ) : (
          // Enterprise
          <>
            {/* Copy - Stała cena pod Segmented Control */}
            <div className="text-center" style={{ marginTop: "6px", paddingBottom: "60px" }}>
              <p style={{
                fontSize: "0.95rem",
            color: "#407EF9",
              fontWeight: "550",
                margin: "0 auto"
              }}>
                Stała cena, wszystkie funkcje w pakiecie.
              </p>
            </div>

          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="text-center mb-4">
                <h3 style={{ fontSize: "2rem", fontWeight: "700", color: "#1f2937", marginBottom: "4px" }}>
                  Wszystko, czego potrzebujesz, by zarządzać dużym zespołem.
                </h3>
                <p style={{ fontSize: "1.1rem", color: "#6b7280", maxWidth: "700px", margin: "0 auto 32px" }}>
                  Stała cena miesięczna, pełen zestaw funkcji i wsparcie przy wdrożeniu.
                </p>
              </div>

              <Row>
                {enterprisePricing.map((plan, idx) => {
                  const isHighlighted = highlightedRange === plan.range;
                  // Określ liczbę kropek (1-6, ostatni ma 6)
                  const dotsCount = idx === 3 ? 6 : idx + 1;
                  
                  return (
                  <Col md={6} lg={3} key={idx} className="mb-4">
                    <Card style={{
                      height: "100%",
                      border: isHighlighted ? "3px solid #407EF9" : "2px solid #e5e7eb",
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      transform: isHighlighted ? "translateY(-5px) scale(1.05)" : "translateY(0)",
                      boxShadow: isHighlighted ? "0 15px 40px rgba(64,126,249,0.3)" : "none",
                      backgroundColor: isHighlighted ? "#f0f7ff" : "white"
                    }}
                    onMouseEnter={(e) => {
                      if (!isHighlighted) {
                        e.currentTarget.style.transform = "translateY(-5px)";
                        e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.1)";
                        e.currentTarget.style.borderColor = "#407EF9";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isHighlighted) {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.borderColor = "#e5e7eb";
                      }
                    }}>
                      <Card.Body style={{ padding: "30px 20px", textAlign: "center" }}>
                        {/* Ikony kropek na górze */}
                        <div style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          gap: "8px",
                          marginBottom: "20px",
                          minHeight: "30px",
                          alignItems: "center"
                        }}>
                          {dotsCount === 6 ? (
                            <>
                              <div style={{ display: "flex", gap: "8px" }}>
                                {Array.from({ length: 3 }).map((_, dotIdx) => (
                                  <div key={dotIdx} style={{
                                    width: "12px",
                                    height: "12px",
                                    borderRadius: "50%",
                                    backgroundColor: "#407EF9"
                                  }} />
                                ))}
                              </div>
                              <div style={{ display: "flex", gap: "8px" }}>
                                {Array.from({ length: 3 }).map((_, dotIdx) => (
                                  <div key={dotIdx + 3} style={{
                                    width: "12px",
                                    height: "12px",
                                    borderRadius: "50%",
                                    backgroundColor: "#407EF9"
                                  }} />
                                ))}
                              </div>
                            </>
                          ) : (
                            <div style={{ display: "flex", gap: "8px" }}>
                              {Array.from({ length: dotsCount }).map((_, dotIdx) => (
                                <div key={dotIdx} style={{
                                  width: "12px",
                                  height: "12px",
                                  borderRadius: "50%",
                                  backgroundColor: "#407EF9"
                                }} />
                              ))}
                            </div>
                          )}
                        </div>

                        {isHighlighted && (
                          <div style={{
                            backgroundColor: "#407EF9",
                            color: "white",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            fontSize: "0.85rem",
                            fontWeight: "600",
                            marginBottom: "20px",
                            display: "inline-block"
                          }}>
                            Polecany dla {employeeCount} pracowników
                          </div>
                        )}
                        
                        <div style={{
                          fontSize: "1.5rem",
                          fontWeight: "600",
                          color: "#6b7280",
                          marginBottom: "5px"
                        }}>
                          {plan.range}
                        </div>
                        <div style={{ 
                          fontSize: "0.85rem", 
                          color: "#9ca3af", 
                          marginBottom: "25px" 
                        }}>
                          pracowników
                        </div>
                        <div style={{
                          fontSize: typeof plan.price === 'number' ? "2.5rem" : "1.3rem",
                          fontWeight: "700",
                          color: "#407EF9",
                          marginBottom: "5px"
                        }}>
                          {typeof plan.price === 'number' ? `${plan.price} zł` : plan.price}
                        </div>
                        {typeof plan.price === 'number' && (
                          <div style={{ fontSize: "0.9rem", color: "#6b7280" }}>
                            netto / miesiąc
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  );
                })}
              </Row>

              <Card style={{
                border: "2px solid #407EF9",
                borderRadius: "12px",
                marginTop: "30px",
                backgroundColor: "#f8f9fa"
              }}>
                <Card.Body style={{ padding: "40px" }}>
                  <h4 style={{
                    fontSize: "1.5rem",
                    fontWeight: "700",
                    color: "#1f2937",
                    marginBottom: "25px",
                    textAlign: "center"
                  }}>
                    W pakiecie:
                  </h4>
                  <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "10.26px",
                    marginBottom: "30px"
                  }}>
                    {[
                      { name: "Ewidencja czasu pracy", description: "pełne zestawienie przepracowanych godzin", icon: Clock },
                      { name: "GPS", description: "weryfikacja obecności w miejscu pracy", icon: MapPin },
                      { name: "Grafik", description: "planowanie zmian i obecności zespołu", icon: Calendar },
                      { name: "Wnioski urlopowe", description: "składanie i zatwierdzanie wniosków urlopowych online", icon: Palmtree },
                      { name: "Raporty i analityka", description: "dane o godzinach, kosztach i efektywności", icon: BarChart3 },
                      { name: "Eksport do PDF i Excel", description: "gotowe raporty do rozliczeń i faktur", icon: FileDown },
                      { name: "Notatka ze zdjęciem", description: "dokumentacja fotograficzna przy rejestracji czasu", icon: Camera },
                      { name: "Aplikacja mobilna", description: "szybki dostęp do grafiku i godzin z telefonu", icon: Smartphone },
                      { name: "Wsparcie techniczne", description: "szybka pomoc i dedykowany opiekun klienta", icon: Headphones },
                      { name: "Pomoc we wdrożeniu", description: "łatwe wdrożenie z pomocą naszego zespołu", icon: Settings }
                    ].map((feature, idx) => {
                      const IconComponent = feature.icon;

                      return (
                        <div key={idx} style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                          backgroundColor: "#ffffff",
                          padding: "14px 16px",
                          borderRadius: "8px",
                          border: "1.5px solid #e5e7eb",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                          transition: "all 0.2s ease",
                          width: "calc(50% - 5.13px)"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f0f7ff";
                          e.currentTarget.style.borderColor = "#407EF9";
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow = "0 4px 8px rgba(64, 126, 249, 0.15)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#ffffff";
                          e.currentTarget.style.borderColor = "#e5e7eb";
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.05)";
                        }}>
                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}>
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0
                            }}>
                              <IconComponent
                                size={17.1}
                                color="#407EF9"
                                strokeWidth={2.5}
                              />
                            </div>
                            <span style={{
                              fontSize: "0.7695rem",
                              color: "#374151",
                              fontWeight: "600"
                            }}>{feature.name}</span>
                          </div>
                          <p style={{
                            fontSize: "0.72675rem",
                            color: "#6b7280",
                            margin: "0 0 0 25.1px",
                            lineHeight: "1.5"
                          }}>{feature.description}</p>
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-center mt-4">
                    <Button
                      href="/kontakt"
                      style={{
                        padding: "15px 50px",
                        backgroundColor: "#407EF9",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "1.1rem",
                        fontWeight: "600",
                        marginBottom: "20px"
                      }}>
                      Skontaktuj się z nami
                    </Button>
                    <p style={{
                      fontSize: "0.9rem",
                      color: "#6b7280",
                      fontWeight: "500",
                      margin: "0"
                    }}>
                      Wspólnie ustalimy, czego dokładnie potrzebuje Twoja firma
                    </p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          </>
        )}

        {/* Link do kalkulatora oszczędności */}
        <div className="text-center" style={{ marginTop: "40px" }}>
          <a
            href="/kalkulator-oszczednosci"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "1.05rem",
              fontWeight: "600",
              color: "#407EF9",
              textDecoration: "none",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "translateX(5px)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "translateX(0)")}
          >
            Oblicz, ile zaoszczędzisz – Kalkulator oszczędności
            <span style={{ fontSize: "1.3rem" }}>→</span>
          </a>
        </div>
      </Container>

      {/* Partners Section */}
      <Partners />

      {/* Case Study Section */}
      <Container className="py-3">
        <Row className="mb-4">
          <Col className="text-center">
            <h2
              style={{
                fontSize: "2.2rem",
                fontWeight: "bold",
                color: "#1f2937",
                marginBottom: "10px",
                marginTop: '80px'
              }}
            >
              Zobacz, jak sprawdzają się eGodziny u naszego klienta:
            </h2>
          </Col>
        </Row>
        <Row className="mb-5 py-2">
          <Col>
            <div
              style={{
                backgroundColor: "#f8f9fa",
                borderRadius: "20px",
                padding: "40px",
                maxWidth: "1000px",
                margin: "0 auto",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                position: "relative",
              }}
            >
              <Row className="align-items-center">
                <Col
                  md={4}
                  className="text-center mb-3 mb-md-0"
                  style={{ paddingRight: "40px" }}
                >
                  <div style={{ marginBottom: "20px" }}>
                    <Image
                      src={require("../images/staldach-bud-logo.png")}
                      alt="Staldach-Bud Logo"
                      style={{
                        width: "150px",
                        height: "auto",
                      }}
                    />
                  </div>
                  <div style={{ marginTop: "25px", fontSize: "16px" }}>
                    <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
                      Mariusz Wolny
                    </div>
                    <div style={{ color: "#666", marginBottom: "3px" }}>
                      Prezes Zarządu
                    </div>
                    <div style={{ color: "#666" }}>STALDACH-BUD sp. z o.o.</div>
                  </div>
                </Col>

                <Col md={8}>
                  <blockquote
                    style={{ margin: 0, fontSize: "22px", lineHeight: "1.6" }}
                  >
                    <p
                      style={{
                        fontStyle: "italic",
                        marginBottom: "25px",
                        color: "#333",
                        fontSize: "24px",
                      }}
                    >
                      "Jako przedsiębiorca wiecznie przytłoczony ilością zadań i
                      obowiązków, nie zdawałem sobie sprawy, że w tak łatwy
                      sposób można poprawić działanie firmy"
                    </p>

                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "bold",
                        marginBottom: "0px",
                        color: "#333",
                        textAlign: "left"
                      }}
                    >
                      Każdego miesiąca oszczędzamy:
                    </div>

                    <div
                      style={{
                        fontSize: "36px",
                        fontWeight: "bold",
                        color: "#407EF9",
                        marginBottom: "20px",
                        textAlign: "left"
                      }}
                    >
                      <AnimatedCounter value={9180} suffix=" zł" />
                    </div>

                    </blockquote>

                  <div style={{ 
                    position: "absolute", 
                    bottom: "40px", 
                    right: "40px" 
                  }}>
                    <a
                      href="/case-study"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "12px",
                        fontSize: "18px",
                        fontWeight: "600",
                        color: "#1f2937",
                        textDecoration: "none",
                        transition: "all 0.3s ease",
                        cursor: "pointer"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "#407EF9";
                        e.currentTarget.style.transform = "translateX(5px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = "#1f2937";
                        e.currentTarget.style.transform = "translateX(0)";
                      }}
                    >
                     Przeczytaj więcej
                      <span style={{ fontSize: "24px" }}>→</span>
                    </a>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Container>

      {/* FAQ Section */}
      <FAQ content={content} />

      <Footer />
    </>
  );
}

export default Pricing2;
